import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  ActionableInsight,
  ActionableInsightsResponse,
  InsightSampleEntity,
} from '../models/actionable-insights.models';
import { mockActionableInsights } from '../sample/actionable-insights-mock-data';
import { InventoryService } from '../services/inventory.service';

/**
 * The whole prototype seam for the insight deck (ONE-3509), in one disposable
 * file.
 *
 * `GET /v1/inventory/insights/actionable/` does not exist yet. This serves it
 * from `sample/`, and then does something less usual: it overwrites the sample
 * numbers with REAL ones for the three rules whose predicate the backend already
 * implements, and stamps every rule with whether it ended up live or invented.
 *
 * ## Why the live overlay lives here and not in the service
 *
 * It was in `ActionableInsightsService` first, and that made the service the
 * problem. PROTOTYPE_RULES section 2 wants one endpoint per widget and a service
 * that is identical to its production form, so that deleting this interceptor is
 * the entire cutover. With the overlay in the service, going live meant editing
 * twenty references across the service, the model and two components, and it is
 * exactly that kind of scattered cleanup that never quite finishes.
 *
 * Everything temporary is now in this file. The service makes one call to one
 * endpoint and is already what it will be in production. Deleting this file, its
 * line in `app.config.ts`, and `sample/actionable-insights-mock-data.ts` is the
 * complete cutover, and nothing in the service or the components has to change.
 *
 * ## No re-entrancy
 *
 * The count queries below go back through the interceptor chain, which is fine:
 * this interceptor only claims the `insights/actionable` path and passes the
 * inventory list URLs straight through to the real API.
 */

/** Rules whose `?insight=` predicate the backend already implements. */
const LIVE_RULES: Record<string, 'users' | 'endpoints'> = {
  no_edr: 'endpoints',
  no_vicarius: 'endpoints',
  no_entra_id: 'users',
};

/** Coverage flags on a canonical row, as a short human list. */
const SOURCE_LABELS: Record<string, string> = {
  seen_by_edr: 'EDR',
  seen_by_xdr: 'XDR',
  seen_by_vicarius: 'Vicarius',
  seen_by_entra_id: 'Entra ID',
  seen_by_tickets: 'Tickets',
  seen_by_alertstats: 'Alerts',
};

export const actionableInsightsMockInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET' || !req.url.includes('/v1/inventory/insights/actionable/')) {
    return next(req);
  }

  const inventory = inject(InventoryService);
  const organizationId = Number(req.params.get('organizationId') ?? 0);

  return of(mockActionableInsights).pipe(
    // Slowest response on the page on purpose: the deck animates its entrance,
    // and a reply in 20ms would hide the loading state real evaluation will have.
    delay(380),
    switchMap((res) => withLiveCounts(inventory, organizationId, res.insights)),
    map(
      (insights) =>
        new HttpResponse<ActionableInsightsResponse>({
          status: 200,
          body: { ...mockActionableInsights, insights },
        }),
    ),
  );
};

/**
 * Replace sample numbers with real ones wherever the backend can answer, and
 * label every rule either way.
 *
 * Scope comes from the same list endpoint with no insight filter, which the
 * backend treats as a no-op and answers with the organisation total. That is a
 * WIDER denominator than the rule's true scope: "endpoints with no EDR agent" is
 * really scoped to endpoints another tool can see, and nothing answers that
 * today. The card's wording ("of N endpoints") states the wider population
 * plainly rather than implying the narrower one.
 */
function withLiveCounts(
  inventory: InventoryService,
  organizationId: number,
  insights: ActionableInsight[],
): Observable<ActionableInsight[]> {
  const live = insights.filter((i) => LIVE_RULES[i.filter_key]);
  if (!organizationId || live.length === 0) {
    return of(insights.map(asSample));
  }

  const count = (tab: 'users' | 'endpoints', insight: string, exceptions?: string) => {
    const call$ =
      tab === 'users'
        ? inventory.getUserInsightCount(organizationId, insight, exceptions)
        : inventory.getEndpointInsightCount(organizationId, insight, exceptions);
    return call$.pipe(catchError(() => of(null)));
  };

  // One scope query per entity list, not per rule: two endpoint rules share a
  // denominator and must never be able to disagree about it.
  const tabs = Array.from(new Set(live.map((i) => LIVE_RULES[i.filter_key])));
  const scopes$ = forkJoin(
    Object.fromEntries(tabs.map((tab) => [tab, count(tab, '')])),
  ) as Observable<Record<string, number | null>>;

  return scopes$.pipe(
    switchMap((scopes) =>
      forkJoin(
        insights.map((insight) => {
          const tab = LIVE_RULES[insight.filter_key];
          if (!tab) {
            return of(asSample(insight));
          }
          return forkJoin({
            affected: count(tab, insight.filter_key),
            accepted: count(tab, insight.filter_key, 'only'),
            rows: inventory
              .getInsightSample(tab, organizationId, insight.filter_key, 3)
              .pipe(catchError(() => of([] as Record<string, unknown>[]))),
          }).pipe(
            map(({ affected, accepted, rows }) => {
              const scope = scopes[tab];
              // Any leg failing drops the whole rule back to sample. A live
              // numerator over a sample denominator would be a worse lie than
              // either, because nothing on screen could reveal the mix.
              if (affected === null || scope === null || scope <= 0) {
                return asSample(insight);
              }
              return {
                ...insight,
                affected_count: affected,
                accepted_count: accepted ?? 0,
                scope_count: scope,
                // Real rows for a real count. The invented ones are dropped
                // rather than shown beside a live number as its members.
                sample_entities: rows.map(toSampleEntity),
                // No prior evaluation exists to diff against.
                new_since_last: null,
                data_source: 'live' as const,
              };
            }),
          );
        }),
      ),
    ),
    catchError(() => of(insights.map(asSample))),
  );
}

/**
 * A canonical entity row as the deck's constellation wants it.
 *
 * The detail line is the sources that DO see the entity, which is the useful
 * half of a coverage finding: "no EDR agent, seen by XDR" tells an operator the
 * host is real and reachable, where the rule name alone only says what is
 * absent. `weight` is null because a coverage gap has no magnitude, so the
 * constellation renders a flat set rather than inventing a ranking.
 */
function toSampleEntity(row: Record<string, unknown>): InsightSampleEntity {
  const name =
    (row['endpoint_name'] as string) ||
    (row['display_name'] as string) ||
    (row['principal_name'] as string) ||
    (row['email'] as string) ||
    'Unnamed';
  const sources = Object.entries(SOURCE_LABELS)
    .filter(([key]) => row[key] === true)
    .map(([, label]) => label);
  return {
    name,
    detail: sources.length > 0 ? `Seen by ${sources.join(', ')}` : 'No source coverage',
    weight: null,
  };
}

/**
 * Every rule this interceptor serves is stamped explicitly, which is what lets
 * the model treat a MISSING flag as live. During the prototype the flag is
 * always present, so absence can only mean the response came from a real
 * backend, and the badge then correctly never fires.
 */
function asSample(insight: ActionableInsight): ActionableInsight {
  return { ...insight, data_source: 'sample' };
}
